import { Controller, FieldValues, useFormState } from 'react-hook-form'

import { Group } from '~/components/component-library'
import { TextInputProps } from '~/components/component-library/TextInput/types'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { Input } from '~/components/inputs/Input'

export const TextInput = <FormType extends FieldValues = FieldValues>({
  control,
  inputName,
  placeholder,
  id,
  validationFunc = () => true,
  autoFocus,
  inputType = 'text',
  autoComplete = 'off',
  optional = false,
  requiredErrorMessage = 'Required',
  onClearInput,
  disabledReason,
  disabled = false,
  isLoading,
  label,
  description,
  footer,
  tooltip,
  onFocus,
  onBlur,
  onKeyDown,
  prefix,
  suffix,
  size = 'base',
}: TextInputProps<FormType>) => {
  const { isSubmitted } = useFormState({ control })

  return (
    <Controller
      control={control}
      name={inputName}
      render={({ field, fieldState }) => {
        const message = visibleError(fieldState.error, {
          isTouched: fieldState.isTouched,
          isEmpty: !field.value,
          isSubmitted,
        })
        return (
          <Group
            label={label}
            secondaryLabel={optional ? 'Optional' : undefined}
            footer={message ? <ErrorFooter message={message} /> : footer}
            description={description}
            tooltip={tooltip}
          >
            <Input
              id={id}
              ref={field.ref}
              placeholder={placeholder}
              autoComplete={autoComplete}
              autoFocus={autoFocus}
              type={inputType}
              name={inputName}
              value={field.value}
              onChange={field.onChange}
              onBlur={() => {
                onBlur?.()
                return field.onBlur()
              }}
              onFocus={() => {
                onFocus?.()
              }}
              onKeyDown={onKeyDown}
              disabledReason={disabledReason}
              disabled={disabled}
              // message drives Input's red border; the text renders in the footer
              error={message}
              showError={false}
              onClearInput={onClearInput}
              isLoading={isLoading}
              prefix={prefix}
              unit={suffix}
              inputSize={size}
            />
          </Group>
        )
      }}
      rules={{
        required: {
          value: !optional,
          message: requiredErrorMessage,
        },
        validate: validationFunc,
      }}
    />
  )
}

TextInput.displayName = 'TextInput'

export type { TextInputProps } from '~/components/component-library/TextInput/types'
